/**
 * Classification enums for games and game categories
 */

/**
 * Tags for game classification including technical and feature-based tags
 */
export enum GameTag {
	// Technical classification tags
	/** Component-based games that can be embedded or used as building blocks */
	Component = 'Komponentti',
	/** Container games that can hold other games or components */
	Container = 'Säiliö',
	/** Complete standalone game experiences */
	Preset = 'Kokonaisuus',

	// Feature-based tags
	/** Educational games focused on learning */
	Educational = 'Opetuksellinen',
	/** Entertainment-focused games */
	Entertainment = 'Viihteellinen',
	/** Games that work as standalone educational material */
	StandaloneEducational = 'Toimii itsenäisenä opetusmateriaalina',
	/** Familiar game types that everyone knows */
	FamiliarGames = 'Kaikille tutut pelit',
	/** Novel, innovative game types */
	InnovativeGames = 'Uudenlaiset pelit',
}

/**
 * Categories for grouping games by difficulty/content type
 */
export enum GameCategory {
	/** Easy, beginner-friendly games */
	EasyGames = 'Helpot pelit',
	/** Games focused on adaptive reading experiences */
	AdaptiveReading = 'Mukautuva lukeminen',
	/** Advanced, complex games */
	AdvancedGames = 'Edistyneet pelit',
}

/**
 * Display text mapping for GameTag enum values
 */
export const GAME_TAG_DISPLAY_TEXT: Record<GameTag, string> = {
	[GameTag.Component]: 'Komponentti',
	[GameTag.Container]: 'Säiliö',
	[GameTag.Preset]: 'Kokonaisuus',
	[GameTag.Educational]: 'Opetuksellinen',
	[GameTag.Entertainment]: 'Viihteellinen',
	[GameTag.StandaloneEducational]: 'Toimii itsenäisenä opetusmateriaalina',
	[GameTag.FamiliarGames]: 'Kaikille tutut pelit',
	[GameTag.InnovativeGames]: 'Uudenlaiset pelit',
};

/**
 * Display text mapping for GameCategory enum values
 */
export const GAME_CATEGORY_DISPLAY_TEXT: Record<GameCategory, string> = {
	[GameCategory.EasyGames]: 'Helpot pelit',
	[GameCategory.AdvancedGames]: 'Edistyneet pelit',
	[GameCategory.AdaptiveReading]: 'Mukautuva lukeminen',
};

/**
 * Manual ordering for technical tags
 */
export const TECHNICAL_TAG_ORDER: GameTag[] = [
	GameTag.Component,
	GameTag.Container,
	GameTag.Preset,
];

/**
 * Manual ordering for feature tags
 */
export const FEATURE_TAG_ORDER: GameTag[] = [
	GameTag.Educational,
	GameTag.Entertainment,
	GameTag.StandaloneEducational,
	GameTag.FamiliarGames,
	GameTag.InnovativeGames,
];

/**
 * Manual ordering for category-based grouping (GameCategory)
 */
export const GAME_CATEGORY_ORDER: GameCategory[] = [
	GameCategory.EasyGames,

	GameCategory.AdvancedGames,
	GameCategory.AdaptiveReading,
];

/**
 * Helper function to get display text for a game tag
 */
export function getGameTagDisplayText(tag: GameTag): string {
	return GAME_TAG_DISPLAY_TEXT[tag];
}

/**
 * Helper function to get display text for a game category
 */
export function getGameCategoryDisplayText(category: GameCategory): string {
	return GAME_CATEGORY_DISPLAY_TEXT[category];
}

/**
 * Group names for organizing tags and categories in dropdowns
 */
export const TAG_GROUP_NAMES = {
	TECHNICAL_TAGS: 'Tekniset tyypit',
	CONTENT_CATEGORIES: 'Sisältökategoriat',
	GAME_FEATURES: 'Pelin ominaisuudet',
} as const;

/**
 * Grouped structure for dropdown display
 */
export interface TagGroup {
	groupName: string;
	items: Array<{
		type: 'tag' | 'category';
		value: GameTag | GameCategory;
		displayText: string;
		available?: boolean; // Optional availability flag for filtering
	}>;
}

/**
 * Get organized tag groups for dropdown display
 */
export function getTagGroups(): TagGroup[] {
	return [
		{
			groupName: TAG_GROUP_NAMES.CONTENT_CATEGORIES,
			items: GAME_CATEGORY_ORDER.map((category) => ({
				type: 'category' as const,
				value: category,
				displayText: GAME_CATEGORY_DISPLAY_TEXT[category],
			})),
		},
		{
			groupName: TAG_GROUP_NAMES.GAME_FEATURES,
			items: FEATURE_TAG_ORDER.map((tag) => ({
				type: 'tag' as const,
				value: tag,
				displayText: GAME_TAG_DISPLAY_TEXT[tag],
			})),
		},
		{
			groupName: TAG_GROUP_NAMES.TECHNICAL_TAGS,
			items: TECHNICAL_TAG_ORDER.map((tag) => ({
				type: 'tag' as const,
				value: tag,
				displayText: GAME_TAG_DISPLAY_TEXT[tag],
			})),
		},
	];
}
